import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormattedNumber } from "react-intl";
import * as _ from "lodash-es";
import IGetShippingOrderTotalTiersDTO from "../../../../models/DTO/IGetShippingOrderTotalTiersDTO";
import IOnGetShippingOrderTotalTiersDTO from "../../../../models/DTO/IOnGetShippingOrderTotalTiersDTO";
import IProcessShippingOrderTotalTiersDTO from "../../../../models/DTO/IProcessShippingOrderTotalTiersDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IShipping_Order_Total_Tier from "../../../../models/IShipping_Order_Total_Tier";
import OwButton from "../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { ConfirmDeleteOverlayFn } from "../../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../../shared/OwDeleteEditActions/OwDeleteEditActions";
import "./OrderTotalTiersSidebar.scss";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    cancelFn: (refreshData?: boolean) => void;
};

type FormData = {
    min_order_total: number;
    max_order_total?: number;
    shipping_amount: number;
};

const createValidationSchema = () => {
    return yup.object().shape({
        min_order_total: yup
            .number()
            .required("Minimum order total is required")
            .min(0, "Minimum order total must be 0 or greater")
            .typeError("Must be a number"),
        max_order_total: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? undefined : value))
            .when("min_order_total", (min_order_total, schema) => {
                return typeof min_order_total === "number"
                    ? schema.min(min_order_total, "Maximum order total must be greater than minimum")
                    : schema;
            })
            .typeError("Must be a number"),
        shipping_amount: yup
            .number()
            .required("Shipping amount is required")
            .min(0, "Shipping amount must be 0 or greater")
            .typeError("Must be a number"),
    });
};

export const OrderTotalTiersSidebar = (props: Props) => {
    const { socket, supplierModel, userModel } = useAppContextNonNull();

    const [createActive, setCreateActive] = useState(false);
    const [shippingOrderTotalTiers, setShippingOrderTotalTiers] = useState<IShipping_Order_Total_Tier[]>([]);
    const [targetDelete, setTargetDelete] = useState<string | null>(null);
    const [selectedTier, setSelectedTier] = useState<IShipping_Order_Total_Tier | null>(null);

    const { control, handleSubmit, reset } = useForm<FormData>({
        resolver: yupResolver(createValidationSchema()) as any,
        defaultValues: {
            min_order_total: 0,
            max_order_total: undefined,
            shipping_amount: 0,
        },
    });

    useEffect(() => {
        socket.on(EndpointsEnum.ShipmentOnGetShippingOrderTotalTiers, onGetShippingOrderTotalTiers);
        socket.on(EndpointsEnum.ShipmentOnProcessShippingOrderTotalTiers, onProcessShippingOrderTotalTiers);

        getShippingOrderTotalTiers();

        return () => {
            socket.removeListener(EndpointsEnum.ShipmentOnGetShippingOrderTotalTiers, onGetShippingOrderTotalTiers);
            socket.removeListener(EndpointsEnum.ShipmentOnProcessShippingOrderTotalTiers, onProcessShippingOrderTotalTiers);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getShippingOrderTotalTiers = () => {
        const payload: IGetShippingOrderTotalTiersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.ShipmentGetShippingOrderTotalTiers, payload);
    };

    const onGetShippingOrderTotalTiers = (data: IOnGetShippingOrderTotalTiersDTO) => {
        const sortedTiers = _.sortBy(data.shippingOrderTotalTiers, ["min_order_total"]);
        setShippingOrderTotalTiers(sortedTiers);
        setCreateActive(false);
    };

    const onProcessShippingOrderTotalTiers = () => {
        getShippingOrderTotalTiers();
    };

    const onSubmit = (data: FormData) => {
        const payload: IProcessShippingOrderTotalTiersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            tiers: [
                {
                    min_order_total: data.min_order_total,
                    max_order_total: data.max_order_total || null,
                    shipping_amount: data.shipping_amount,
                    supplier_id: supplierModel.id,
                },
            ],
        };

        socket.emit(EndpointsEnum.ShipmentProcessShippingOrderTotalTiers, payload);

        setCreateActive(true);
        reset();
    };

    const deleteTier = () => {
        if (selectedTier) {
            const payload: IProcessShippingOrderTotalTiersDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                tiers: [
                    {
                        ...selectedTier,
                        deleteFlag: true,
                    },
                ],
            };

            socket.emit(EndpointsEnum.ShipmentProcessShippingOrderTotalTiers, payload);

            setTargetDelete(null);
            setSelectedTier(null);
        }
    };

    const cancelDelete = () => {
        setTargetDelete(null);
        setSelectedTier(null);
    };

    const confirmDelete = (tier: IShipping_Order_Total_Tier, index: number) => {
        setTargetDelete(`row-${index}`);
        setSelectedTier(tier);
    };

    const cancel = (refreshData?: boolean) => {
        props.cancelFn(refreshData);
    };

    return (
        <div className={"OrderTotalTiersSidebar p-2 position-relative"} id={"OrderTotalTiersSidebar"}>
            <ConfirmDeleteOverlayFn
                targetElem={targetDelete}
                handleConfirm={deleteTier}
                handleCancel={cancelDelete}
                skipMainView={true}
                skipLeftAdjustment={true}
            />

            <div className={"col-12"}>
                <div className="text-end">
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                            cancel();
                        }}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle className={"mb-2"} leftIconName={"fad fa-layer-group"} title="shipping_order_total_tiers" />
                <hr />
            </div>

            <form onSubmit={handleSubmit(onSubmit as any)}>
                <div className={"row"}>
                    <div className={"d-flex col-12"}>
                        <div>
                            <OwLabel title={"min_order_total"} />
                            <Controller
                                name="min_order_total"
                                control={control}
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                render={({ field: { ref: _ref, ...field } }) => (
                                    <OwInputWrapper
                                        {...field}
                                        name="min_order_total"
                                        fn={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
                                        value={field.value}
                                    />
                                )}
                            />
                        </div>
                        <div className={"ms-2"}>
                            <OwLabel title={"max_order_total"} />
                            <Controller
                                name="max_order_total"
                                control={control}
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                render={({ field: { ref: _ref, ...field } }) => (
                                    <OwInputWrapper
                                        {...field}
                                        name="max_order_total"
                                        fn={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
                                        value={field.value || ""}
                                    />
                                )}
                            />
                        </div>
                        <div className={"ms-2"}>
                            <OwLabel title={"shipping_amount"} />
                            <Controller
                                name="shipping_amount"
                                control={control}
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                render={({ field: { ref: _ref, ...field } }) => (
                                    <OwInputWrapper
                                        {...field}
                                        name="shipping_amount"
                                        fn={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
                                        value={field.value}
                                    />
                                )}
                            />
                        </div>
                        {/* <div className={"ms-2"}>
                            <OwLabel title={supplierModel.has_shipping_by_weight_state ? "shipping_state" : "shipping_zone"} />
                            {supplierModel.has_shipping_by_weight_state ? (
                                <Controller
                                    name="shipping_state"
                                    control={control}
                                    render={({ field }) => (
                                        <OwSelectOption
                                            {...field}
                                            name="shipping_state"
                                            titleProp="title"
                                            valueProp="value"
                                            options={shippingStates}
                                            fn={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                                            selectedOption={{ shipping_state: field.value }}
                                            selectedOptionProp="shipping_state"
                                        />
                                    )}
                                />
                            ) : (
                                <Controller
                                    name="shipping_zone_id"
                                    control={control}
                                    render={({ field }) => (
                                        <OwSelectOption
                                            {...field}
                                            name="shipping_zone_id"
                                            titleProp="name"
                                            valueProp="id"
                                            options={shippingZones}
                                            fn={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(Number(e.target.value))}
                                            selectedOption={{ shipping_zone_id: field.value }}
                                            selectedOptionProp="shipping_zone_id"
                                        />
                                    )}
                                />
                            )}
                        </div> */}
                        <div className={"ms-2"}>
                            <OwLabel title={" "} skipTranslate={true} />
                            <div>
                                <OwButton title={"create"} fn={() => {}} type="submit" active={createActive} disableOnActive={true} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <div className={"mt-4"}>
                <div className={"row"}>
                    <div className={"col"}></div>
                    <div className={"col "}>
                        <OwLabel title={"min_order_total"} isUnderline={true} />
                    </div>
                    <div className={"col text-center"}>
                        <OwLabel title={"max_order_total"} isUnderline={true} />
                    </div>
                    <div className={"col text-end"}>
                        <OwLabel title={"shipping_amount"} isUnderline={true} />
                    </div>
                    {/* <div className={"col text-end"}>
                        <OwLabel title={supplierModel.has_shipping_by_weight_state ? "shipping_state" : "shipping_zone"} />
                    </div> */}
                </div>

                {shippingOrderTotalTiers.map((tier, index: number) => {
                    return (
                        <div key={tier.guid} className={"row py-2 tier-row"} id={`row-${index}`}>
                            <div className={"col"}>
                                <OwDeleteEditActions
                                    isEditMode={false}
                                    onTrashClickFn={() => {
                                        confirmDelete(tier, index);
                                    }}
                                    onEditClickFn={() => {}}
                                    hideEdit={true}
                                    className={"me-2"}
                                />
                            </div>
                            <div className={"col text-center"}>
                                <FormattedNumber
                                    value={tier.min_order_total}
                                    style={"currency"}
                                    currency={"USD"}
                                    maximumFractionDigits={2}
                                    minimumFractionDigits={2}
                                    currencySign={"standard"}
                                    currencyDisplay={"symbol"}
                                />
                            </div>
                            <div className={"col text-center"}>
                                {tier.max_order_total && tier.max_order_total > 0 ? (
                                    <FormattedNumber
                                        value={tier.max_order_total}
                                        style={"currency"}
                                        currency={"USD"}
                                        maximumFractionDigits={2}
                                        minimumFractionDigits={2}
                                        currencySign={"standard"}
                                        currencyDisplay={"symbol"}
                                    />
                                ) : (
                                    "--"
                                )}
                            </div>
                            <div className={"col text-end"}>
                                <FormattedNumber
                                    value={tier.shipping_amount}
                                    style={"currency"}
                                    currency={"USD"}
                                    maximumFractionDigits={2}
                                    minimumFractionDigits={2}
                                    currencySign={"standard"}
                                    currencyDisplay={"symbol"}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
